import {
  type Faction,
} from '@dissidium/contracts';
import { integer } from '../kernel/primitives.ts';

export type FactionId = Faction;
export type HolderId = Faction | 'LOC';

export interface OfficialProfile {
  readonly id: string;
  readonly name: string;
  readonly holder: HolderId;
  readonly title: string;
  readonly leverage: string;
  readonly coreDemands: string;
}

export const Z03_OFFICIALS: Readonly<Record<HolderId, OfficialProfile>> = {
  SEN: {
    id: 'person:clerk-ossa-vel',
    name: 'Clerk Talla Vel',
    holder: 'SEN',
    title: 'Clerk of Municipal Registers',
    leverage: 'Civic writ, debt bond cancellation, and registry recognition',
    coreDemands: 'Restores grain lift while holding contractors accountable for arrears',
  },
  BRZ: {
    id: 'person:depot-sereth-kall',
    name: 'Depot Officer Sereth Kall',
    holder: 'BRZ',
    title: 'Depot Officer',
    leverage: 'Military escort, armory repair, and road transit passes',
    coreDemands: 'Restores grain lift and charges requisition costs to civilian merchants',
  },
  REE: {
    id: 'person:measurer-bera-nall',
    name: 'Measurer Bera Nall',
    holder: 'REE',
    title: 'Canal Measurer',
    leverage: 'Labour contributions, grain leaf release, and warehouse access',
    coreDemands: 'Restores grain lift and protects canal worker rations against seizure',
  },
  SAL: {
    id: 'person:factor-deren-pell',
    name: 'Factor Deren Pell',
    holder: 'SAL',
    title: 'Harbour & Toll Factor',
    leverage: 'Commercial credit advances, quay notes, and vessel clearance',
    coreDemands: 'Restores grain lift and secures preferred berth liens on lower landing',
  },
  LOC: {
    id: 'person:speaker-maran',
    name: 'Speaker Maran',
    holder: 'LOC',
    title: 'Toll Council Speaker',
    leverage: 'Local community consent, toll bypass, and neighbourhood shelter',
    coreDemands: 'Restores grain lift without inviting outside military garrison',
  },
};

export type ConcessionStatus = 'active' | 'investigated' | 'suspended' | 'dismantled' | 'contained';

export interface ConcessionState {
  readonly factorId: string;
  readonly factorName: string;
  /**
   * Concession guarantor is the faction backing Tavren Noll's burial concession.
   * This is explicitly distinct from the zone's administrative public holder.
   */
  readonly guarantor: FactionId;
  status: ConcessionStatus;
  debtBondsIssued: number;
  limeRevenueActive: boolean;
  metalSalvageActive: boolean;
  stolenBodiesCount: number;
}

export type ObligationType = 'debt-bond' | 'supply-quota' | 'compulsory-labour' | 'berth-lien';

export interface FactionObligation {
  readonly id: string;
  readonly faction: FactionId;
  readonly type: ObligationType;
  readonly title: string;
  readonly description: string;
  readonly burdenUnits: number;
  resolved: boolean;
}

export interface LocalFactionState {
  readonly zoneId: 'Z03';
  /** The administrative authority collecting dues and presiding over court/toll */
  readonly publicHolder: HolderId;
  readonly official: OfficialProfile;
  /** Concession operating the Quiet Kilns with its distinct political guarantor */
  concession: ConcessionState;
  obligations: FactionObligation[];
  standing: Record<FactionId, number>;
  favour: Record<FactionId, number>;
  witnessesProtected: boolean;
  melianSeparatedFromCorpseWork: boolean;
  persecutionLevel: number; // 0 to 5
}

export interface CreateLocalFactionOptions {
  readonly publicHolder?: HolderId;
  readonly concessionGuarantor?: FactionId;
  readonly initialStanding?: Partial<Record<FactionId, number>>;
  readonly initialFavour?: Partial<Record<FactionId, number>>;
  readonly initialPersecution?: number;
}

export function createLocalFactionState(options: CreateLocalFactionOptions = {}): LocalFactionState {
  const publicHolder: HolderId = options.publicHolder ?? 'REE';
  const concessionGuarantor: FactionId = options.concessionGuarantor ?? 'SEN';

  const defaultStanding: Record<FactionId, number> = {
    SEN: 0,
    BRZ: 0,
    REE: 0,
    SAL: 0,
    ...options.initialStanding,
  };

  return {
    zoneId: 'Z03',
    publicHolder,
    official: Z03_OFFICIALS[publicHolder],
    concession: {
      factorId: 'person:tavren-noll',
      factorName: 'Tavren Noll',
      guarantor: concessionGuarantor,
      status: 'active',
      debtBondsIssued: 96,
      limeRevenueActive: true,
      metalSalvageActive: true,
      stolenBodiesCount: 9,
    },
    obligations: [],
    standing: defaultStanding,
    favour: { SEN: 0, BRZ: 0, REE: 0, SAL: 0, ...options.initialFavour },
    witnessesProtected: false,
    melianSeparatedFromCorpseWork: false,
    persecutionLevel: options.initialPersecution ?? 3,
  };
}

export type FactionFavourEvent =
  | { readonly type: 'favour-earned'; readonly faction: FactionId; readonly amount: number; readonly reason: string; readonly balance: number }
  | { readonly type: 'favour-spent'; readonly faction: FactionId; readonly amount: number; readonly service: string; readonly balance: number };

export function spendFactionFavour(
  state: LocalFactionState,
  faction: FactionId,
  amount: number,
  service: string,
): FactionFavourEvent {
  const value = integer(amount);
  if (value <= 0) throw new Error('Faction favour price must be a positive integer');
  if (state.favour[faction] < value) {
    throw new Error(`${faction} service ${service} costs ${value} favour; balance is ${state.favour[faction]}`);
  }
  state.favour[faction] = integer(state.favour[faction] - value);
  return { type: 'favour-spent', faction, amount: value, service, balance: state.favour[faction] };
}

/** Verifies that the public holder is distinct from the concession guarantor */
export function isPublicHolderDistinct(state: LocalFactionState): boolean {
  return (state.publicHolder as string) !== (state.concession.guarantor as string);
}

export interface DismantleOutcome {
  readonly success: boolean;
  readonly reason?: string;
  readonly status: ConcessionStatus;
  readonly guarantorStandingDelta: number;
  readonly persecutionDelta: number;
}

/**
 * Public dismantling of Tavren Noll's burial concession.
 * Per docs/world/04-zones.md:
 * - Requires at least two independent evidence types.
 * - Requires secured witnesses.
 * - Concession is closed, lime/salvage stopped.
 * - Guarantor suffers exposure.
 * - Persecution falls ONLY if evidence explicitly separates Melian's lawful practice from corpse-work.
 */
export function dismantleConcession(
  state: LocalFactionState,
  evidenceTypes: readonly string[],
  witnessesSecured: boolean,
  separatesMelian: boolean,
): DismantleOutcome {
  if (evidenceTypes.length < 2) {
    return {
      success: false,
      reason: 'Public dismantling requires at least two independent evidence types',
      status: state.concession.status,
      guarantorStandingDelta: 0,
      persecutionDelta: 0,
    };
  }

  if (!witnessesSecured) {
    return {
      success: false,
      reason: 'Public dismantling requires secured witnesses before closing the concession',
      status: state.concession.status,
      guarantorStandingDelta: 0,
      persecutionDelta: 0,
    };
  }

  state.concession.status = 'dismantled';
  state.concession.limeRevenueActive = false;
  state.concession.metalSalvageActive = false;
  state.witnessesProtected = true;

  // Concession guarantor loses political standing due to exposure
  const guarantor = state.concession.guarantor;
  state.standing[guarantor] = integer(state.standing[guarantor] - 3);

  let persecutionDelta = 0;
  if (separatesMelian) {
    state.melianSeparatedFromCorpseWork = true;
    state.persecutionLevel = Math.max(0, state.persecutionLevel - 2);
    persecutionDelta = -2;
  }

  return {
    success: true,
    status: 'dismantled',
    guarantorStandingDelta: -3,
    persecutionDelta,
  };
}

export interface ContainOutcome {
  readonly success: boolean;
  readonly reason?: string;
  readonly status: ConcessionStatus;
}

/**
 * Private containment of the Quiet Kilns operation.
 * Per docs/world/04-zones.md:
 * Forces Noll out and bargains with Sela Venn to stop animation, preserve records,
 * and accept supervision or exile.
 * A credible independent guarantor is required; "she promises" is insufficient.
 */
export function containConcessionPrivately(
  state: LocalFactionState,
  independentGuarantor: FactionId,
): ContainOutcome {
  if (independentGuarantor === state.concession.guarantor) {
    return {
      success: false,
      reason: 'Private containment requires an independent guarantor distinct from Noll’s current patron',
      status: state.concession.status,
    };
  }

  state.concession.status = 'contained';
  state.concession.limeRevenueActive = false;
  state.concession.metalSalvageActive = false;

  return {
    success: true,
    status: 'contained',
  };
}

/**
 * Adds an authored obligation. Per CT06, only explicit authored commitments
 * may bind the faction/district to new obligations.
 */
export function addFactionObligation(
  state: LocalFactionState,
  obligation: FactionObligation,
): LocalFactionState {
  if (state.obligations.some(o => o.id === obligation.id)) {
    throw new Error(`Obligation ${obligation.id} is already recorded`);
  }
  state.obligations.push(obligation);
  return state;
}
